import { useState } from "react";
import {
  Database, ChevronDown, ChevronUp, Brain, Target,
  AlertTriangle, Unlock, Sparkles, Terminal
} from "lucide-react";
import {
  PROJECTS_FULL, PPTX_TOP3, DATA_TIERS, API_ENDPOINTS, NEWS_CITATIONS
} from "../../data/presentationData";
import { SW, Badge, RadarMini, CopyBtn, CitationBar } from "./shared";

export function Section5() {
  const [exp, setExp] = useState(null);
  const [showTiers, setShowTiers] = useState(false);
  const [showAPIs, setShowAPIs] = useState(false);
  const [sortBy, setSortBy] = useState("total");
  const [viewMode, setViewMode] = useState("cards");
  const pptxIds = PPTX_TOP3.map(p => p.id);
  const tierA = PROJECTS_FULL.filter(p => p.tier === "A");
  const tierB = PROJECTS_FULL.filter(p => p.tier === "B");
  const tierC = PROJECTS_FULL.filter(p => p.tier === "C");
  const sorted = [...PROJECTS_FULL].sort((a, b) => b[sortBy] - a[sortBy]);

  const ProjectCard = ({ p }) => {
    const isExp = exp === p.id;
    return (
      <div data-testid={`project-card-${p.id}`}
        className={`rounded-2xl border cursor-pointer card-expand ${isExp ? "border-blue-400 shadow-lg bg-blue-50/50 col-span-2" : "border-slate-200 hover:border-blue-300 hover:shadow-md bg-white card-lift"}`}
        onClick={() => setExp(isExp ? null : p.id)}>
        <div className="p-5">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {p.badge && <Badge color={p.badgeColor}>{p.badge}</Badge>}
                {(p.pptxPick || pptxIds.includes(p.id)) && <span className="text-[10px] font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">PPTX PICK</span>}
              </div>
              <h3 className="font-bold text-lg text-slate-900 mt-2">{p.name}</h3>
              <p className="text-sm text-slate-600 mt-1" style={{ fontFamily: 'var(--font-body)' }}>{p.desc}</p>
            </div>
            <div className="ml-3 flex flex-col items-center flex-shrink-0">
              <div className="text-2xl font-bold text-[#005BBB]">{p.total}</div>
              <div className="text-[10px] text-slate-400">/20</div>
              <RadarMini data={p} size={100} />
            </div>
          </div>
          {isExp && p.fullDesc && (
            <div className="mt-4 pt-4 border-t border-blue-200 space-y-4 tooltip-enter">
              <div className="bg-blue-100/40 rounded-xl p-4"><p className="text-sm text-slate-700 leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>{p.fullDesc}</p></div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <h4 className="font-semibold text-sm text-blue-700 mb-2 flex items-center gap-1.5"><Brain className="w-4 h-4" /> Methodology</h4>
                  <p className="text-sm text-slate-700" style={{ fontFamily: 'var(--font-body)' }}>{p.methodology}</p>
                  <h4 className="font-semibold text-sm text-blue-700 mt-3 mb-2 flex items-center gap-1.5"><Database className="w-4 h-4" /> Data</h4>
                  <p className="text-sm text-slate-700" style={{ fontFamily: 'var(--font-body)' }}>{p.dataNote}</p>
                  {p.dataTier && p.dataTier.map((dt, di) => (
                    <div key={di} className={`mt-2 text-xs px-3 py-2 rounded-lg ${dt.tier === 1 ? "bg-emerald-100 text-emerald-800" : dt.tier === 2 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>
                      <span className="font-bold">Tier {dt.tier}:</span> {dt.sources.join(", ")}
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-emerald-700 mb-2 flex items-center gap-1.5"><Unlock className="w-4 h-4" /> Min Viable</h4>
                  <p className="text-sm text-slate-700" style={{ fontFamily: 'var(--font-body)' }}>{p.minViable}</p>
                  <h4 className="font-semibold text-sm text-amber-700 mt-3 mb-2 flex items-center gap-1.5"><Target className="w-4 h-4" /> Stretch</h4>
                  <p className="text-sm text-slate-700" style={{ fontFamily: 'var(--font-body)' }}>{p.stretch}</p>
                  {p.whyMatters && <><h4 className="font-semibold text-sm text-purple-700 mt-3 mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Why It Matters</h4><p className="text-sm text-slate-700" style={{ fontFamily: 'var(--font-body)' }}>{p.whyMatters}</p></>}
                  <h4 className="font-semibold text-sm text-slate-600 mt-3 mb-1 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Risk: {p.risk}</h4>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mt-2">
            <div className="flex gap-2 text-[10px] text-slate-400" style={{ fontFamily: 'var(--font-body)' }}><span>DA:{p.da}</span><span>MF:{p.mf}</span><span>PR:{p.pr}</span><span>TL:{p.tl}</span></div>
            {isExp ? <ChevronUp className="w-4 h-4 text-blue-500" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
      </div>
    );
  };

  return (
    <SW id="section-5">
      <div className="mb-6 stagger-1">
        <span className="text-[#005BBB] text-xs font-bold tracking-[0.2em] uppercase" style={{ fontFamily: 'var(--font-body)' }}>The Projects</span>
        <h2 className="text-4xl md:text-5xl font-bold mt-3 text-slate-900">16 Research Opportunities, Analytically Scored</h2>
        <p className="text-slate-500 mt-3 text-lg max-w-4xl" style={{ fontFamily: 'var(--font-body)' }}>
          Each project scored across four dimensions: <span className="font-semibold text-slate-700">Data Access</span>, <span className="font-semibold text-slate-700">Methodology Fit</span>, <span className="font-semibold text-slate-700">Partner Readiness</span>, and <span className="font-semibold text-slate-700">Timeline</span>. Grouped by feasibility tier (A{'\u2013'}C). Click any project to explore.
        </p>
      </div>
      <div className="bg-[#FFD500]/10 border-2 border-[#FFD500]/30 rounded-2xl p-6 mb-8 stagger-2" data-testid="starting-points-banner">
        <div className="flex items-start gap-4">
          <Sparkles className="w-6 h-6 text-[#C5A258] flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-slate-900 text-lg mb-2">Starting Points, Not Limits</h3>
            <p className="text-sm text-slate-700 leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
              These 16 projects are <span className="font-semibold">initial suggestions</span> based on publicly available information about the Diia ecosystem. The true abundance of research opportunities becomes clear only through <span className="font-semibold">direct conversations with Ukrainian partners</span> {'\u2014'} project leads at MDT, faculty at KSE and KPI, field operators at HALO Trust and UNDP. Each conversation will reveal sub-projects, dataset nuances, and policy questions we can't see from the outside. The Diia ecosystem spans <span className="font-semibold">200+ digital services</span> touching every sector of Ukrainian society {'\u2014'} health, education, defense, finance, housing, employment, energy, and more. Every service generates data. Every dataset has stories waiting to be told.
            </p>
            <p className="text-sm text-[#005BBB] font-semibold mt-3" style={{ fontFamily: 'var(--font-body)' }}>
              These 16 projects are the tip of the iceberg. The real question isn't "Can we find enough to study?" {'\u2014'} it's "How do we choose from the abundance?"
            </p>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mb-5 stagger-3">
        <button onClick={() => setViewMode("cards")} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${viewMode === "cards" ? "bg-[#005BBB] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Card View</button>
        <button onClick={() => setViewMode("table")} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${viewMode === "table" ? "bg-[#005BBB] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Table View</button>
        <button data-testid="toggle-data-tiers" onClick={() => { setShowTiers(!showTiers); setShowAPIs(false); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${showTiers ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Data Tiers</button>
        <button data-testid="toggle-api-endpoints" onClick={() => { setShowAPIs(!showAPIs); setShowTiers(false); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${showAPIs ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}><Terminal className="w-3.5 h-3.5 inline mr-1" />APIs</button>
      </div>
      {viewMode === "cards" && (
        <div className="space-y-8">
          {[{ label: "Tier A \u2014 High Feasibility", desc: "Strong data access, clear methodology fit, ready to start", items: tierA, color: "emerald" },
            { label: "Tier B \u2014 Medium Feasibility", desc: "Viable with some partnership effort or restricted data access", items: tierB, color: "amber" },
            { label: "Tier C \u2014 Exploratory", desc: "Higher barriers but unique research value; long-term opportunities", items: tierC, color: "slate" },
          ].map((tier) => (
            <div key={tier.label}>
              <div className="flex items-center gap-3 mb-3">
                <span className={`px-3 py-1 rounded-lg text-xs font-bold bg-${tier.color}-100 text-${tier.color}-800`}>{tier.label}</span>
                <span className="text-xs text-slate-500" style={{ fontFamily: 'var(--font-body)' }}>{tier.desc} ({tier.items.length} projects)</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {tier.items.map(p => <ProjectCard key={p.id} p={p} />)}
              </div>
            </div>
          ))}
        </div>
      )}
      {viewMode === "table" && (
        <div className="rounded-2xl border border-slate-200 overflow-hidden mb-6">
          <div className="bg-slate-50 px-4 py-3 flex gap-2 items-center text-xs text-slate-600 border-b" style={{ fontFamily: 'var(--font-body)' }}>
            Sort: {["total", "da", "mf", "pr", "tl"].map(k => (
              <button key={k} data-testid={`sort-${k}`} onClick={() => setSortBy(k)} className={`px-2.5 py-1 rounded-lg ${sortBy === k ? "bg-[#005BBB] text-white" : "bg-white border text-slate-600 hover:bg-slate-100"}`}>
                {k === "total" ? "Total" : k === "da" ? "Data" : k === "mf" ? "Method" : k === "pr" ? "Partners" : "Timeline"}
              </button>
            ))}
          </div>
          <table className="w-full text-sm" style={{ fontFamily: 'var(--font-body)' }}>
            <thead className="bg-slate-100"><tr><th className="text-left p-3 font-medium text-slate-600">Project</th><th className="p-3 w-12">DA</th><th className="p-3 w-12">MF</th><th className="p-3 w-12">PR</th><th className="p-3 w-12">TL</th><th className="p-3 w-14">Total</th><th className="p-3 w-12">Tier</th></tr></thead>
            <tbody>
              {sorted.map((p) => (
                <tr key={p.id} className={`border-t cursor-pointer hover:bg-blue-50 ${p.tier === "A" ? "bg-emerald-50" : p.tier === "B" ? "bg-amber-50/50" : "bg-slate-50"}`} onClick={() => { setViewMode("cards"); setExp(p.id); }}>
                  <td className="p-3 font-medium">{p.name}{p.desc && <span className="block text-xs text-slate-400 mt-0.5">{p.desc}</span>}{(p.pptxPick || pptxIds.includes(p.id)) && <span className="ml-1 text-[9px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">PPTX</span>}</td>
                  <td className="p-3 text-center">{p.da}</td><td className="p-3 text-center">{p.mf}</td><td className="p-3 text-center">{p.pr}</td><td className="p-3 text-center">{p.tl}</td>
                  <td className="p-3 text-center font-bold">{p.total}</td>
                  <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-xs font-bold ${p.tier === "A" ? "bg-emerald-200 text-emerald-800" : p.tier === "B" ? "bg-amber-200 text-amber-800" : "bg-slate-200 text-slate-600"}`}>{p.tier}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showTiers && (
        <div className="space-y-4 tooltip-enter mb-6 mt-4">
          {Object.entries(DATA_TIERS).map(([key, tier]) => (
            <div key={key} className="rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 flex items-center gap-2" style={{ backgroundColor: tier.color + "12", borderLeft: `4px solid ${tier.color}` }}>
                <span className="font-bold text-sm" style={{ color: tier.color }}>{tier.label}</span>
                <span className="text-xs text-slate-500" style={{ fontFamily: 'var(--font-body)' }}>{tier.desc}</span>
                <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: tier.color + "20", color: tier.color }}>{tier.sources.length} sources</span>
              </div>
              <div className="p-3 grid grid-cols-3 gap-2">
                {tier.sources.map((s, si) => (
                  <div key={si} className="bg-white border border-slate-100 rounded-lg p-3 text-xs">
                    <div className="font-bold text-slate-800">{s.name}</div>
                    <div className="text-slate-500 mt-1" style={{ fontFamily: 'var(--font-body)' }}>{s.notes}</div>
                    {s.url && <div className="text-blue-600 mt-1 truncate">{s.url}</div>}
                    {s.route && <div className="text-amber-600 mt-1">Route: {s.route}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {showAPIs && (
        <div className="bg-slate-900 rounded-2xl p-5 mb-6 mt-4 tooltip-enter">
          <h4 className="text-emerald-400 font-bold text-sm mb-3 flex items-center gap-1.5"><Terminal className="w-4 h-4" /> Test These Today</h4>
          <div className="space-y-2">
            {API_ENDPOINTS.map((ep, i) => (
              <div key={i} className="bg-slate-800/70 rounded-lg p-3 flex items-center gap-3">
                <code className="text-emerald-300 text-xs flex-1 font-mono truncate">{ep.endpoint}</code>
                <CopyBtn text={ep.endpoint} />
                <span className="text-slate-500 text-[10px] flex-shrink-0 max-w-[200px] truncate" style={{ fontFamily: 'var(--font-body)' }}>{ep.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-sky-50 border border-sky-200 rounded-2xl p-5 mt-6 stagger-4">
        <p className="text-sky-800 text-sm" style={{ fontFamily: 'var(--font-body)' }}>
          <span className="font-bold">Key point:</span> "Multiple Tier A projects can start today with open data {'\u2014'} no MOU required. And these 16 are just the projects we can identify from the outside. Direct conversations with Ukrainian partners will reveal dozens more."
        </p>
      </div>
      <CitationBar citations={NEWS_CITATIONS.projects} />
    </SW>
  );
}
